import {clamp, entries, indexBy, isNonNullish, pick, pipe, sortBy, values} from 'remeda'
import {match, P} from 'ts-pattern'
import {O} from 'ts-toolbelt'
import {z} from 'zod'
import {Dist, DistributionLabel, DistLogistic} from '../chart'
import {DistDrawn} from '../chart/distributionDrawn'
import {compactObj, concat, groupMapObj, isNullOrEmpty, mapEntries} from '../collection'
import {
  QuestionBounds,
  QuestionCfg,
  QuestionForecastType,
  QuestionResultsQuestion,
  ResolutionCfg,
  zQuestionForecastType,
  zAmountType,
  QuestionAmountType,
  ResolutionValue,
  QuestionHardBounds,
  comparisonCfgKey
} from './question'
import {SubqCfg, SubqCfgWithId, SubqType} from './subq'
import {Resolution as R} from './resolution'
import {notNull, zx} from '../type'
import {geomOfWholePct} from '../agg'
import {Bounds} from './bounds'
import {doSafe, doTo} from '../promise'
import {Fmt} from './Fmt'
import {queryStringSerializer} from '../queryStringSerializer'
import {DateTime} from 'luxon'
import {QRComparison} from './results/questionResultsLayout'
import {
  ForecastType,
  ForecastCfg,
  zForecastTypes,
  isLogisticValid,
  ForecastLogistic,
  ForecastLogisticValid,
  ForecastFetchedBase
} from './forecastTypes'
import {fmtNum} from '../format'
import {Rnge} from '../range'

export namespace Forecast {
  const asymptotic0To1 = (value: number) => 1 / (1 + Math.exp(-value))

  const asymptoticFactor = 0.05
  export const clampLikelihood = (v?: number, mode: 'manual' | 'auto' = 'auto') =>
    v == null ? undefined
    : mode == 'manual' ? clamp(v, {min: 0, max: 100})
    : match(v)
        .with(P.number.gt(99), v => 99 + asymptotic0To1((v - 99) * asymptoticFactor))
        .with(P.number.lt(1), v => 1 - asymptotic0To1((-v - 1) * asymptoticFactor))
        .otherwise(v => v)

  export const isTypeCompatible = (forecastType: ForecastType, questionType: QuestionForecastType) =>
    match(forecastType)
      .with('logistic', 'drawn', () => questionType == 'amount')
      .with('likelihood', () => questionType == 'likelihood')
      .exhaustive()

  export const toQuestionType = (t: ForecastType) =>
    match(t)
      .with('logistic', 'drawn', () => 'amount' as const)
      .with('likelihood', () => 'likelihood' as const)
      .exhaustive()

  export const defaultForecast = (
    f: (
      | {type: 'amount' | 'logistic' | 'drawn'; bounds: QuestionBounds; amountType: QuestionAmountType}
      | {type: 'likelihood'}
    ) & {
      code?: string // questions don't have codes, sub subquestions all do (even the "specials"/main)
    }
  ): ForecastCfg =>
    match(f)
      .with({type: P.union('logistic', 'drawn', 'amount')}, ({type, ...f}) => {
        const forecastType = match(type)
          .with('amount', 'logistic', () => 'logistic' as const)
          .with('drawn', () => 'drawn' as const)
          .exhaustive()
        return zForecastTypes[forecastType].parse({type: forecastType, boundsVersion: '2', ...f})
      })
      .with({type: 'likelihood'}, f => zForecastTypes.likelihood.parse(f))
      .exhaustive()

  /**
   * Get the question in the same format as sub-question resolutions. Makes it easier to re-use display code upstream
   * Does not take into account ts (timestamp), caller should implement `getForecasts` for that
   */
  export const getQuestionResolutionForecast = <F extends ForecastFetchedBase, G extends UseForecastGroup<F>>({
    question: q,
    getForecasts
  }: Pick<ResolutionForecastProps<F, G>, 'question' | 'getForecasts'>): ResolutionForecasts<F, G> | undefined => {
    const question_id = q.id
    const questionForecasts = getForecasts(
      match(q)
        .with({forecastType: 'amount'}, q => ({type: 'amount', amountType: q.amountType, question_id}) as const)
        .with({forecastType: 'likelihood'}, () => ({type: 'likelihood', question_id}) as const)
        .exhaustive()
    )
    if (!questionForecasts) return undefined

    const resolution = {label: q.title, enableForecasts: !q.disableQuestionForecast} as ResolutionCfg
    const fmt: ResolutionFmt = (v, options) => {
      const formats = Fmt.getOption(
        q.forecastType == 'amount' ?
          {
            forecastType: 'amount',
            amountType: q.amountType,
            numberFormat: q.numberFormat,
            unitLabel: q.unitLabel ?? '',
            mode: 'absolute'
          }
        : {
            forecastType: 'likelihood',
            mode: 'absolute'
          }
      )
      return Fmt.fmt(v, formats[options?.size ?? 'default'])
    }
    const resAgg = aggValue({q, fs: questionForecasts?.forecasts ?? []})
    const forecasts =
      questionForecasts?.forecasts?.map(
        f =>
          ({
            ...f,
            value: getValue({f: f.cfg, q})
          }) as ResolutionForecast<F>
      ) ?? []

    const comparisons = doSafe(q.comparisons?.[comparisonCfgKey(questionForecasts.group)], cs =>
      mapEntries(cs, (id, c) => (c ? {...c, id} : undefined)).filter(notNull)
    )

    return {
      resolution,
      resolutionValue: q.resolutionValues[R.resolutionKeyString({type: q.forecastType})],
      comparisons,
      fmt,
      cfg: q,
      group: questionForecasts,
      resAgg,
      shouldVizRelative: false,
      forecasts,
      code: undefined
    }
  }

  /**
   * Processes forecasts into subquestion resolutions, given a question and subquestion.
   * Does not take into account ts (timestamp), caller should implement `getForecasts` for that
   */
  export const getResolutionForecasts = <F extends ForecastFetchedBase, G extends UseForecastGroup<F>>({
    getForecasts,
    question,
    subq,
    parentSubq,
    sqId
  }: ResolutionForecastProps<F, G>): ResolutionForecasts<F, G>[] => {
    const groupKeyObj = {
      question_id: question.id,
      subquestion_id: sqId
    }
    const {colors, chart, aggVersion} = question
    const {subquestionType} = subq
    const parentQ = parentSubq ?? question
    /** the question shaped properties given the context of this subquestion. Can come from the parent, question or the subquestion itself */
    const resQuestion = {
      ...pick(
        subquestionType == 'cause' ? parentQ : subq, // not this is where we get the forecast settings from, which is different to how we key resolutions
        ['forecastType', 'unitLabel', 'numberFormat', 'amountType', 'defaultBounds', 'hardBounds']
      ),
      aggVersion,
      title: subq.title,
      colors,
      chart,
      // defaultBounds: Bounds.withDefaults((subquestionType == 'cause' ? question : subq).defaultBounds).bounds,
      subquestionType,
      comparisons: subq.comparisons
    } as ForecastQuestionCfg

    const resolutions = R.resolutions(
      {
        forecastType: parentQ.forecastType,
        consequenceResolutions: parentSubq ? parentSubq.consequenceResolutions : question.resolutions
      },
      subq
    )
    const parentForecasts = getForecasts({
      question_id: question.id,
      subquestion_id: parentSubq?.id,
      type: parentQ.forecastType,
      amountType: parentQ.amountType,
      code: parentSubq ? parentSubq.forecastType : undefined
    })

    const parentAgg = doSafe(parentForecasts, qf => {
      const group = aggValue({q: resQuestion, fs: qf.forecasts ?? []})
      return {
        group,
        you: doSafe(parentForecasts?.myForecast, f => getValue({f: f.cfg, q: resQuestion})),
        user: (id: string) => doSafe(qf.userForecast(id), f => getValue({f: f.cfg, q: resQuestion})),
        type: 'question'
      } satisfies ForecastBaselineAgg
    })

    type RBasic = Pick<ResolutionForecasts<F>, 'cfg' | 'group' | 'resolution' | 'questionAgg'>
    const resolutionForecasts = sortBy(
      resolutions
        .map(
          ({id: code, resolutionDisplay: resolution}) =>
            ({
              group: getForecasts(
                match(resQuestion)
                  .with(
                    {forecastType: 'likelihood'},
                    _ => ({...groupKeyObj, code, type: 'likelihood'}) satisfies ForecastGroupKey
                  )
                  .with(
                    {forecastType: 'amount'},
                    q => ({...groupKeyObj, code, type: 'amount', amountType: q.amountType}) satisfies ForecastGroupKey
                  )
                  .exhaustive()
              ),
              cfg: resQuestion,
              resolution,
              questionAgg: subquestionType == 'cause' && code != 'likelihood' ? parentAgg : undefined
            }) as RBasic
        )
        .filter(r => r.resolution.enableForecasts && r.group),
      r => r.resolution.position ?? Number.MAX_SAFE_INTEGER
    )

    const resolutionByCode = indexBy(resolutionForecasts, r => r.group?.group?.code ?? '_')

    const resolutionsWithSpecials = pipe(subq, cfg => {
      const likelihood =
        //&& cfg.likelihood.enableForecasts
        cfg.forecastType == 'likelihood' ?
          ({
            group: getForecasts({...groupKeyObj, code: 'likelihood', type: 'likelihood'}),
            resolution: cfg.likelihood,
            cfg: {
              forecastType: 'likelihood',
              colors,
              ...pick(cfg, [
                'title',
                'subTitle',
                'description',
                'unitLabel',
                'unitLabelLong',
                'subquestionType',
                'comparisons'
              ]),
              chart
            } satisfies ForecastQuestionCfg
          } as RBasic)
        : undefined
      if (likelihood && likelihood.resolution.color == null)
        likelihood.resolution.color = resolutionByCode['cause-true']?.resolution.color
      const amount = match(cfg)
        .with(
          {forecastType: 'amount'},
          cfg =>
            ({
              group: getForecasts({...groupKeyObj, code: 'amount', type: 'amount', amountType: cfg.amountType}),
              resolution: cfg.amount,
              cfg: {
                forecastType: 'amount',
                ...pick(cfg, [
                  'unitLabel',
                  'unitLabelLong',
                  'numberFormat',
                  'title',
                  'amountType',
                  'subTitle',
                  'description',
                  'subquestionType',
                  'comparisons'
                ]),
                colors,
                chart,
                defaultBounds: Bounds.withDefaults(cfg.defaultBounds, Bounds.defaultForType(cfg.amountType)).bounds,
                hardBounds: cfg.hardBounds,
                aggVersion
              } satisfies ForecastQuestionCfg
            }) as RBasic
        )
        .otherwise(() => undefined)
      return concat(amount, likelihood, resolutionForecasts)
    })
      .filter(r => !r.resolution.deleted && r.group?.forecasts) // r.resolution.enableForecasts &&
      .map(r => {
        const comparisons = pipe(r.cfg?.comparisons?.[comparisonCfgKey(r.group.group)], cs => {
          if (!cs) return
          return mapEntries(cs, (id, c) => (c ? {...c, id} : undefined)).filter(notNull)
        })
        return {
          ...r,
          code: r.group.group.code!,
          resolutionValue: question.resolutionValues[R.resolutionKeyString(r.group.group)],
          comparisons
        } as RBasic & Pick<ResolutionForecasts<F>, 'code' | 'resolutionValue' | 'comparisons'>
      })

    type RBaseline = Pick<
      ResolutionForecasts<F, G>,
      'cfg' | 'group' | 'resolution' | 'questionAgg' | 'baselineAgg' | 'code' | 'resolutionValue'
    >
    const resolutionsWithBaseline: RBaseline[] = resolutionsWithSpecials.map(r => {
      // derive consequence baseline from true/false/qLikelihood combo
      if (
        parentQ.forecastType == 'likelihood' &&
        subq.forecastType == 'likelihood' &&
        subq.subquestionType == 'consequence' &&
        parentAgg != null &&
        isBaselineCodeConsequence(r.code)
      ) {
        const calcBaseline = (
          getValue: (v: RBasic) => number | undefined,
          parentValue: number | undefined,
          override?: ForecastBaselineOverride
        ): number | undefined => {
          const aggLikelihood = (code: string) => {
            const fcs = resolutionsWithSpecials.find(r =>
              doSafe(r?.group?.group, g => g.code == code && g.type == 'likelihood')
            )
            return fcs ? getValue(fcs) : undefined
          }
          const agg = {
            'question-true': aggLikelihood('question-true'),
            'question-false': aggLikelihood('question-false'),
            ...override
          }
          if (agg['question-false'] == null || agg['question-true'] == null) return undefined
          return baselineProbability(parentValue, agg['question-true'], agg['question-false'])
        }
        const q = r.cfg
        const res = {
          ...r,
          baselineAgg: {
            type: 'consequence-regardless',
            you: calcBaseline(fcs => doSafe(fcs.group?.myForecast, f => getValue({f: f.cfg, q})), parentAgg.you),
            group: calcBaseline(fcs => aggValue({q: resQuestion, fs: fcs.group?.forecasts ?? []}), parentAgg.group),
            user: (id: string) =>
              calcBaseline(
                fcs => doSafe(fcs.group?.userForecast(id), f => getValue({f: f.cfg, q})),
                parentAgg.user(id)
              ),
            // derive the baseline based on the user's forecast for this resolution
            youWith: override =>
              calcBaseline(fcs => doSafe(fcs.group?.myForecast, f => getValue({f: f.cfg, q})), parentAgg.you, override)
          } satisfies ForecastBaselineAgg
        } as RBaseline
        return res
      }
      if (subq.subquestionType == 'cause' && isBaselineCodeCause(r.code) && parentAgg != null)
        return {...r, baselineAgg: parentAgg} as RBaseline
      return r as RBaseline
    })

    const resolutionsWithRelative = resolutionsWithBaseline.map(r => {
      const shouldVizRelative =
        r.baselineAgg?.group != null && chart.relative == true && r.group?.group.code != 'likelihood'

      const groupForecasts = r.group?.forecasts?.map((f: F) => {
        const res: F = match(f.cfg)
          // derive likelihood from relativePoints (if it exists), otherwise derive relativePoints from likelihood
          .with({type: 'likelihood'}, cfg => {
            const {likelihood: likelihoodRaw, relativePoints: relativePointsRaw} = cfg
            const userBaseline = f.created_by ? r.baselineAgg?.user(f.created_by) : undefined
            if (userBaseline == null) return f
            const likelihood =
              relativePointsRaw != null ? clampLikelihood(userBaseline + relativePointsRaw) : likelihoodRaw
            const relativePoints = relativePointsRaw ?? (likelihood != null ? likelihood - userBaseline : undefined)
            return {
              ...f,
              cfg: {
                ...f.cfg,
                ...{relativePoints, likelihood}
              }
            }
          })
          .otherwise(() => f)
        return res
      })

      const resForecasts = groupForecasts?.map(f => {
        const value = getValue({f: f.cfg, q: r.cfg})
        const userBaseline = f.created_by ? r.baselineAgg?.user(f.created_by) : undefined
        const valueRelative = value != null && userBaseline != null ? value - userBaseline : undefined
        return {...f, value, valueRelative}
      })

      const fmt: ResolutionFmt = (v, options) => {
        const {size, mode} = {mode: 'absolute', size: 'default', ...options} as const
        const formats = Fmt.getOption(
          r.cfg.forecastType == 'amount' ?
            {
              forecastType: 'amount',
              amountType: r.cfg.amountType,
              numberFormat: r.cfg.numberFormat,
              unitLabel: r.cfg.unitLabel ?? '',
              mode
            }
          : {forecastType: 'likelihood', mode}
        )
        return Fmt.fmt(v, formats[size])
      }

      return {
        ...r,
        resAgg: aggValue({q: r.cfg, fs: groupForecasts ?? []}),
        shouldVizRelative,
        forecasts: resForecasts,
        fmt
      } as ResolutionForecasts<F, G>
    })

    return resolutionsWithRelative
  }

  export const baselineProbability = (question?: number, ifTrue?: number, ifFalse?: number) =>
    question == null || ifTrue == null || ifFalse == null ?
      undefined
    : (((question / 100) * ifTrue) / 100 + ((1 - question / 100) * ifFalse) / 100) * 100

  /** Group the flat list of forecasts based on ones that will aggregate together
   * group key now includes the forecast type (i.e. amount/likelihood) because we group before we know the subquestion config */
  export const group = <T extends ForecastFetchedBase>({
    forecasts,
    isMine
  }: {
    forecasts: T[]
    isMine?: (f: T) => boolean
  }): Record<string, UseForecastGroup<T>> =>
    groupMapObj(
      forecasts.filter(f => f.question_id != null),
      f => {
        const common = {
          question_id: f.question_id!,
          subquestion_id: f.subquestion_id ?? undefined,
          code: f.cfg.code,
          ts: f.ts
        }
        return groupKey(
          match(f.cfg)
            .with(
              {type: P.union('logistic', 'drawn')},
              f => ({type: 'amount', ...common, amountType: f.amountType}) satisfies ForecastGroupKey
            )
            .with({type: 'likelihood'}, f => ({type: 'likelihood', ...common}) satisfies ForecastGroupKey)
            .exhaustive()
        )
      },
      (fs, groupKey) => {
        const myForecast = isMine ? fs.find(isMine) : undefined
        const group = forecastGroupKeyValue(groupKey)
        return {
          forecasts: fs.map(f => ({
            ...f,
            key: naturalKey({...group, created_by: f.created_by!})
          })),
          group,
          myForecast,
          userForecast: (id: string) => fs.find(f => f.created_by == id)
        } satisfies UseForecastGroup<T>
      }
    )

  export const getValue = ({f, q}: {f: ForecastCfg; q: ForecastQuestionCfgForValue}): number | undefined =>
    match(q)
      .with(
        {forecastType: 'likelihood'},
        () =>
          match(f)
            .with({type: 'likelihood'}, f => f.likelihood)
            .otherwise(() => undefined) as number | undefined
      )
      .with(
        {forecastType: 'amount'},
        q =>
          match(f)
            .with({type: P.union('logistic', 'drawn')}, f => doSafe(amountDist({f, q}), d => d.percentile50))
            .otherwise(() => undefined) as number | undefined
      )
      .otherwise(() => undefined)

  /** basic aggregation of a forecast. Filters out forecasts that have been excluded/expired */
  export const aggValue = ({q, fs}: {q: ForecastQuestionCfgForValue; fs: ForecastFetchedBase[]}): number | undefined =>
    match(q)
      .with({forecastType: 'likelihood'}, q =>
        geomOfWholePct(
          fs
            .filter(shouldShow)
            .map(f =>
              match(f.cfg)
                .with({type: 'likelihood'}, f => f.likelihood)
                .otherwise(() => undefined)
            )
            .filter(isNonNullish)
        )
      )
      .with(
        {forecastType: 'amount'},
        q =>
          Dist.agg(
            fs
              .filter(shouldShow)
              .map(f => amountDist({f: f.cfg, q}))
              .filter(notNull),
            {
              aggVersion: q.aggVersion,
              hardBounds: Bounds.toTuple(q.hardBounds),
              displayBounds: Bounds.toTuple(q.defaultBounds)
            }
          ).percentile50
      )
      .otherwise(() => undefined)

  export const amountConfigured = (f: ForecastFetchedBase['cfg']) =>
    match(f)
      .with({type: 'logistic'}, f => isLogisticValid(f))
      .with({type: 'drawn'}, f => f.points.length > 0)
      .with({type: 'likelihood'}, () => undefined)
      .exhaustive()

  export const amountDist = ({
    f,
    q,
    options
  }: {
    f: ForecastFetchedBase['cfg']
    q: Pick<ForecastQuestionCfgAmount, 'defaultBounds' | 'hardBounds'>
    options?: Dist.ToDiscreteOptions & {debugName?: string}
  }): (Dist.PdfForAgg & Dist.DiscreteDistribution) | undefined => {
    const res = doSafe(
      match(f)
        .with({type: 'logistic'}, f =>
          isLogisticValid(f) ?
            DistLogistic.logisticToPdf(Logistic.toDistLogisticCfg({f, q}), {
              displayBounds: {...q.defaultBounds, ...f.bounds}
            })
          : undefined
        )
        .with({type: 'drawn'}, f =>
          DistDrawn.cfgToPdf({points: f.points, hardBounds: f.hardBounds ?? q.hardBounds, tails: f.tails})
        )
        .with({type: 'likelihood'}, () => undefined)
        .exhaustive(),
      d => ({...d, ...Dist.pdfToDiscrete(d, options)})
    )
    return res
  }

  /**
   * the natural key for forecasts, helps us make edits to the question and be able to use the compatible forecasts
   * this is a combination of both likelihood and amount types so that it easily serializes. but we re-declare as a poly type after
   */
  export const zForecastGroupKey = z.object({
    type: zQuestionForecastType,
    question_id: z.string(),
    subquestion_id: zx.nullish.string,
    code: zx.nullish.string,
    /** nullish means latest forecasts */
    ts: zx.nullish.num,
    /** for amounts this is mandatory, but for likelihoods it must be missing */
    amountType: zx.nullishToUndefined(zAmountType).optional()
  }) // strip to be very consistent as a lookup
  /** equivalent to ForecastGroupKey, but non-poly for persisting and logic when you don't care which type */
  export type ForecastGroupKeyFlat = z.infer<typeof zForecastGroupKey>

  type ForecastGroupKeyCommon = Pick<ForecastGroupKeyFlat, 'question_id' | 'subquestion_id' | 'code' | 'ts'>
  type ForecastGroupKeyLikelihood = {type: 'likelihood'} & ForecastGroupKeyCommon
  type ForecastGroupKeyAmount = {type: 'amount'; amountType: QuestionAmountType} & ForecastGroupKeyCommon
  export type ForecastGroupKey = ForecastGroupKeyLikelihood | ForecastGroupKeyAmount

  const groupKeySerializer = queryStringSerializer(zForecastGroupKey)

  /** a unique string for a forecast group */
  export const groupKey = (group: ForecastGroupKey) =>
    groupKeySerializer.serialize(
      zForecastGroupKey.strip().parse(
        doTo(group as ForecastGroupKeyFlat, ({amountType, ...g}) =>
          compactObj({
            ...g,
            // clean up the default number so we are consistent with our key lookup even if we aren't above
            amountType: amountType == 'number' ? undefined : amountType
          })
        )
      )
    )
  export const forecastGroupKeyValue = (key: string) => groupKeySerializer.deserialize(key) as ForecastGroupKey

  const zForecastNaturalKey = zForecastGroupKey.extend({created_by: z.string()})
  type ForecastNaturalKey = z.infer<typeof zForecastNaturalKey>

  /** a natural key for the forecast. If the forecaster makes an update, you can use this to match */
  const naturalKeySerializer = queryStringSerializer(zForecastNaturalKey)
  export const naturalKey = (f: ForecastNaturalKey) =>
    naturalKeySerializer.serialize(
      doTo(f, ({amountType, ...g}) =>
        compactObj({
          ...g,
          // clean up the default number so we are backwards compatible with existing keys
          amountType: amountType == 'number' ? undefined : amountType
        })
      )
    )
  export const naturalKeyValue = (key: string) => naturalKeySerializer.deserialize(key)

  export const getForecasts = <T extends ForecastFetchedBase>(
    byGroup: Record<string, UseForecastGroup<T>>,
    group: ForecastGroupKey
  ) => {
    const key = groupKey(group)
    const fcs = byGroup?.[key]
    return {
      ...((fcs ? fcs : (
        {
          forecasts: [],
          myForecast: undefined,
          userForecast: () => undefined,
          group
        }
      )) satisfies UseForecastGroup),
      group
    }
  }

  export const isExpired = ({cfg: {status}, ts}: Pick<ForecastFetchedBase, 'cfg' | 'ts'>) => {
    if (!status.expired) return false
    const expiredTs = DateTime.fromISO(status.expired.at).toMillis()
    const tsCurrent = ts == null ? DateTime.utc().toMillis() : ts
    return expiredTs != null && tsCurrent > expiredTs
  }

  /** whether the forecast should be shown in the UI and included in aggregation. Often not filtered in the db because some parts of the ui should have it */
  export const shouldShow = (f?: Pick<ForecastFetchedBase, 'cfg' | 'ts'>) =>
    f != null && f.cfg.status.excluded == null && !isExpired(f)

  type BaselineCodeConsequence = 'question-true' | 'question-false'

  const isBaselineCodeConsequence = (code?: string): code is BaselineCodeConsequence =>
    code != null && ['question-true', 'question-false'].includes(code)

  const isBaselineCodeCause = (code?: string) => code && !['likelihood', 'amount'].includes(code)

  export const isMainForecastCode = (code: string | undefined) => !code || ['likelihood', 'amount'].includes(code)

  type ScoreCommon = {
    value: number
    /**
     * score between -1 (worst) and 1 (best). Not saved or aggregated, just used for coloring and sorting accuracy results
     */
    score: number
  }
  type ScoreLikelihood = ScoreCommon & {
    type: 'likelihood'
    actual: boolean
  }

  type ScoreAmount = ScoreCommon & {type: 'amount'; actual50th: number; error?: number}
  export type Score = ScoreLikelihood | ScoreAmount

  export const scoreLikelihood = (/** between 0 and 100 */ value: number, actual: boolean): ScoreLikelihood => {
    const score0To1 = actual ? value / 100 : 1 - value / 100
    const score = score0To1 * 2 - 1
    return {type: 'likelihood', value: value, score, actual}
  }

  export const scoreAmount = (amount50th: number, actual50th: number): ScoreAmount => {
    const error = amount50th - actual50th
    const percentDifference = error == 0 ? 0 : Math.abs(error) / actual50th
    const score = 2.0 / Math.exp(percentDifference) - 1
    return {
      type: 'amount',
      value: amount50th,
      actual50th,
      error,
      score
    }
  }
}

export type ForecastGroupKey = Forecast.ForecastGroupKey
export type UseForecastGroup<T extends ForecastFetchedBase = ForecastFetchedBase> = {
  forecasts?: T[]
  userForecast: (id: string) => T | undefined
  myForecast?: T
  /** the forecast value. For amounts this is the 50th percentile value. Fore likelihoods, its the likelihood value */
  group: ForecastGroupKey
}

type ForecastQuestionBase = O.Optional<
  Pick<
    QuestionCfg,
    'publish' | 'title' | 'subTitle' | 'colors' | 'chart' | 'unitLabel' | 'unitLabelLong' | 'comparisons'
  >,
  'publish'
> & {subquestionType?: SubqType}

export type ForecastQuestionCfgAmount = {forecastType: 'amount'; defaultBounds: QuestionBounds} & ForecastQuestionBase &
  Pick<QuestionCfg, 'unitLabel' | 'unitLabelLong' | 'numberFormat' | 'amountType' | 'aggVersion' | 'hardBounds'>
export type ForecastQuestionCfgLikelihood = {forecastType: 'likelihood'} & ForecastQuestionBase

/**
 * The aspects of a question/sub-question that the forecast depends on. This derived from both the question and the sub-question to make sense in isolation
 */
export type ForecastQuestionCfg = ForecastQuestionCfgAmount | ForecastQuestionCfgLikelihood
export type ForecastQuestionCfgForValue =
  | Pick<ForecastQuestionCfgAmount, 'forecastType' | 'aggVersion' | 'hardBounds' | 'defaultBounds'>
  | Pick<ForecastQuestionCfgLikelihood, 'forecastType'>

export type ForecastQuestionAgg = {you?: number; group?: number; user: (id: string) => number | undefined}
type ForecastBaselineOverride = Partial<Record<string, number>>
export type ForecastBaselineAgg = ForecastQuestionAgg & {
  type: 'question' | 'consequence-regardless'
  youWith?: (props: ForecastBaselineOverride) => number | undefined
}

export namespace Logistic {
  export const forecastWithDefaults = (
    f: ForecastLogistic,
    question?: Pick<QuestionCfg, 'defaultBounds' | 'hardBounds'>
  ) => {
    const bounds = {...question?.defaultBounds, ...f.bounds}
    const [start, end] = Bounds.toTuple(bounds)
    const w = Rnge.width([start!, end!])
    return {
      location: start! + w / 2,
      scale: w / 15,
      ...f,
      bounds
      // no hard bounds as a default because we always get this from the question for forecasts now
    }
  }

  /** convert from our persisted cfg to the standard logistic cfg */
  export const toDistLogisticCfg = ({
    f,
    q
  }: {
    f: ForecastLogisticValid
    q: Pick<QuestionCfg, 'hardBounds'>
  }): DistLogistic.LogisticCfg => ({
    ...pick(f, ['location', 'scale', 'skew']),
    hardBounds: f.hardBounds ?? q?.hardBounds,
    ...(f.tails ?
      {
        left: f.tails.left?.pct != null ? {pct: f.tails.left.pct / 100, mode: 'bound'} : undefined,
        right: f.tails.right?.pct != null ? {pct: f.tails.right.pct / 100, mode: 'bound'} : undefined
      }
    : {})
  })

  export const get50thFromCfg = ({f, q}: {f: ForecastLogisticValid; q: Pick<QuestionCfg, 'hardBounds'>}) =>
    Dist.pdfToDiscrete(DistLogistic.logisticToPdf(toDistLogisticCfg({f, q}))).percentile50

  export const percentileLabels = (
    dist: Dist.DiscreteDistribution,
    options?: {showTitle?: boolean; percentiles?: number[]; debugName?: string}
  ): DistributionLabel[] => {
    const {percentiles} = {percentiles: [0.1, 0.9], ...options}
    const res = percentiles
      .map(percentile => {
        const x = dist.percentileX(percentile, options)
        if (!x) return null
        return {
          x,
          title: options?.showTitle ? fmtNum(percentile * 100, '#o') : undefined,
          type: 'xBottom',
          lineProps: {strokeWidth: 2, opacity: 0.2}
        } satisfies DistributionLabel
      })
      .filter(notNull)
    return res
  }

  export const label50th = (
    dist: Dist.DiscreteDistribution,
    options?: {
      format?: Fmt.Option
      color?: string
      type?: 'mainPoint' | 'point'
      noLabel?: boolean
      showLabelAtTop?: boolean
      subTitle?: string
      relativeToX?: number
    }
  ): DistributionLabel[] => {
    const x = dist.percentile50
    const {color, format, noLabel, relativeToX} = options ?? {}
    const type = options?.type ?? 'mainPoint'
    const label =
      x ?
        concat(
          {
            x,
            title: noLabel ? undefined : Fmt.fmt(relativeToX ? x - relativeToX : x, format),
            type,
            color,
            subTitle: options?.subTitle,
            ...(options?.showLabelAtTop ? {labelProps: {y: 0}} : {})
          } satisfies DistributionLabel,
          type == 'mainPoint' ?
            ({
              x,
              type: 'xTop',
              color,
              lineProps: {strokeWidth: 3, opacity: 1}
            } satisfies DistributionLabel)
          : undefined
        )
      : options?.subTitle ?
        [
          {
            x: 0,
            title: '',
            subTitle: options.subTitle,
            color,
            type
          } satisfies DistributionLabel
        ]
      : []
    return label
  }

  const labelBoundsCommon = {
    type: 'xBottom',
    lineProps: {strokeWidth: 1, opacity: 0.2},
    labelProps: {titleFontWeight: 'normal'}
  } as const

  export const labelBounds = ({bounds, color}: {bounds?: QuestionBounds; color?: string}): DistributionLabel[] =>
    bounds ?
      concat(
        bounds.min ?
          ({
            ...labelBoundsCommon,
            x: bounds.min,
            color
          } satisfies DistributionLabel)
        : undefined,
        bounds.max ?
          ({
            ...labelBoundsCommon,
            x: bounds.max,
            color
          } satisfies DistributionLabel)
        : undefined
      )
    : []

  export const labelComparison = ({x, color, title}: {x: number; color: string; title: string}) =>
    ({
      type: 'xBottom',
      x,
      color,
      title,
      labelProps: {horizontalAnchor: 'start'},
      titleProps: {opacity: 0.7},
      lineProps: {strokeWidth: 2, opacity: 0.5}
    }) as DistributionLabel
}

export type ResolutionForecastProps<
  F extends ForecastFetchedBase = ForecastFetchedBase,
  G extends UseForecastGroup<F> = UseForecastGroup<F>
> = {
  /** the root question, where in the hierarchy the subq is */
  question: QuestionResultsQuestion
  subq: SubqCfg
  parentSubq?: SubqCfgWithId
  sqId: string
  getForecasts: (group: ForecastGroupKey) => G | undefined
}

export type ResolutionForecast<F extends ForecastFetchedBase = ForecastFetchedBase> = F & {
  value: number
  valueRelative?: number
}

type ResolutionFmt = (
  v: number | undefined,
  options?: {
    size?: Fmt.Size
    mode?: 'relative' | 'absolute'
  }
) => string

export type ResolutionForecasts<
  F extends ForecastFetchedBase = ForecastFetchedBase,
  G extends UseForecastGroup<F> = UseForecastGroup<F>
> = {
  resolution: Partial<ResolutionCfg>
  resolutionValue?: ResolutionValue
  comparisons?: QRComparison[]
  group: G
  code: string | undefined
  cfg: ForecastQuestionCfg
  /** when the question shares the same units, this will be populated for comparison */
  questionAgg?: ForecastQuestionAgg
  /* A baseline aggregate value for comparison. Populated when it makes sense */
  baselineAgg?: ForecastBaselineAgg
  subquestion?: SubqCfg
  /** aggregate of the resolutions forecast values */
  resAgg: number | undefined
  forecasts: ResolutionForecast<F>[]
  shouldVizRelative: boolean
  fmt: ResolutionFmt
}
